import React, { useState } from 'react';
import axios from 'axios';
import { MessageCircle, ArrowRight, Check, Calendar, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { MarketingConsentCheckbox } from './MarketingConsentCheckbox';
import { serviceTypes } from '../data/mock';
import { toast } from 'sonner';
import { trackFormSubmission, trackCalendlyView, trackCTAClick } from '../utils/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Consultation = () => {
  const [formData, setFormData] = useState({
    name: '',
    organization: '',
    email: '',
    phone: '',
    serviceType: '',
    message: '',
    marketing_consent: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCalendly, setShowCalendly] = useState(false);

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const serviceLabel = serviceTypes.find(s => s.value === formData.serviceType)?.label || formData.serviceType;
      
      await axios.post(`${BACKEND_URL}/api/contact`, {
        name: formData.name,
        email: formData.email,
        company: `${formData.organization} | Phone: ${formData.phone} | Interest: ${serviceLabel}`,
        message: formData.message || 'Requesting a consultation',
        marketing_consent: formData.marketing_consent,
        source_form: 'homepage_consultation',
      });
      
      toast.success('Thank you! Your request has been received.');
      trackFormSubmission('consultation', { service_type: formData.serviceType, organization: formData.organization });
      setFormData({ name: '', organization: '', email: '', phone: '', serviceType: '', message: '', marketing_consent: false });
      setShowCalendly(true);
      trackCalendlyView();
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Calendly Modal after submission
  if (showCalendly) {
    return (
      <section id="consultation" className="py-16 sm:py-24 bg-slate-900 relative overflow-hidden">
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-slate-950 rounded-2xl p-6 sm:p-10 border border-slate-800 text-center">
            {/* Close Button */}
            <button
              onClick={() => setShowCalendly(false)}
              className="absolute top-4 right-4 text-slate-400 hover:text-white"
            >
              <X className="h-6 w-6" />
            </button>

            {/* Success Icon */}
            <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-6">
              <Check className="h-8 w-8 text-emerald-400" />
            </div>

            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              Thank You!
            </h2>
            
            <p className="text-slate-400 text-lg mb-2">
              Your request has been received.
            </p>
            
            <p className="text-white text-xl font-semibold mb-8">
              Want to move faster? Schedule your consultation now.
            </p>

            {/* Calendly Embed */}
            <div className="rounded-xl overflow-hidden border border-slate-700 mb-6">
              <iframe
                src="https://calendly.com/coachbrandonward-1/schedule_with_cbw?hide_gdpr_banner=1&background_color=0f172a&text_color=ffffff&primary_color=06b6d4"
                width="100%"
                height="650"
                frameBorder="0"
                title="Schedule a Leadership Strategy Session"
                className="bg-slate-950"
              />
            </div>

            <p className="text-slate-500 text-sm">
              Or wait for our team to reach out within 24-48 hours.
            </p>
            
            <button
              onClick={() => setShowCalendly(false)}
              className="text-cyan-400 hover:text-cyan-300 text-sm mt-4 underline"
            >
              Submit another request
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="consultation" className="py-16 sm:py-24 bg-slate-900 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-cyan-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-violet-500/5 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Left Content */}
          <div>
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/20 mb-6">
              <MessageCircle className="h-4 w-4 text-cyan-400" />
              <span className="text-cyan-400 text-sm font-semibold">Free Consultation</span>
            </div>

            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 sm:mb-6">
              Schedule a Leadership
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-violet-400">
                Strategy Session
              </span>
            </h2>

            <p className="text-slate-400 text-base sm:text-lg mb-8 leading-relaxed">
              Let's explore your leadership, workforce, team, or AI goals 
              and identify the right next step for your organization. No pressure, no pitch—just 
              a focused conversation about your needs.
            </p>

            {/* What You'll Get */}
            <div className="space-y-4 mb-8">
              <h4 className="text-white font-semibold">What to Expect:</h4>
              {[
                'A focused 20-30 minute strategy session',
                'Clarity on your goals and challenges',
                'Recommendations tailored to your situation',
                'Information on available workforce funding',
                'No obligation or pressure'
              ].map((item) => (
                <div key={item} className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-cyan-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="h-3 w-3 text-cyan-400" />
                  </div>
                  <span className="text-slate-300 text-sm">{item}</span>
                </div>
              ))}
            </div>

            {/* Funding Note */}
            <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
              <p className="text-emerald-300 text-sm">
                <span className="font-semibold">Funding Available:</span> Eligible organizations may qualify for workforce training funding or reimbursement.
              </p>
            </div>
          </div>

          {/* Right Form */}
          <div className="bg-slate-950 rounded-2xl p-6 sm:p-8 border border-slate-800">
            <h3 className="text-xl font-bold text-white mb-2">Request Your Free Consultation</h3>
            <p className="text-slate-400 text-sm mb-6">
              Fill out the form and we'll reach out within 24-48 hours.
            </p>

            <form onSubmit={handleSubmit} data-testid="consultation-form" className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-slate-400 text-sm mb-2 block">Your Name *</label>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Smith"
                    required
                    data-testid="consultation-name-input"
                    className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500"
                  />
                </div>
                <div>
                  <label className="text-slate-400 text-sm mb-2 block">Organization *</label>
                  <Input
                    name="organization"
                    value={formData.organization}
                    onChange={handleChange}
                    placeholder="Your Company or Organization"
                    required
                    data-testid="consultation-organization-input"
                    className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-slate-400 text-sm mb-2 block">Email *</label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@company.com"
                    required
                    className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500"
                  />
                </div>
                <div>
                  <label className="text-slate-400 text-sm mb-2 block">Phone</label>
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="(555) 123-4567"
                    className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500"
                  />
                </div>
              </div>

              <div>
                <label className="text-slate-400 text-sm mb-2 block">What support are you looking for?</label>
                <select
                  name="serviceType"
                  value={formData.serviceType}
                  onChange={handleChange}
                  className="w-full h-10 px-3 rounded-md bg-slate-900 border border-slate-700 text-white focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500"
                >
                  <option value="" className="bg-slate-900">Select an option...</option>
                  {serviceTypes.map((service) => (
                    <option key={service.value} value={service.value} className="bg-slate-900">
                      {service.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-slate-400 text-sm mb-2 block">Tell us about your goals or challenges</label>
                <Textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="What would you like to accomplish? What challenges are you facing?"
                  rows={4}
                  className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500 resize-none"
                />
              </div>

              <MarketingConsentCheckbox
                checked={formData.marketing_consent}
                onChange={(v) => setFormData({ ...formData, marketing_consent: v })}
                note="We'll respond to your inquiry either way."
                testId="consultation-marketing-consent"
              />

              <Button
                type="submit"
                disabled={isSubmitting}
                data-testid="consultation-submit-button"
                className="w-full bg-white text-slate-950 hover:bg-slate-100 font-semibold py-6 transition-all duration-300 disabled:opacity-50"
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-slate-950/30 border-t-slate-950 rounded-full animate-spin" />
                    Submitting...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    Schedule a Leadership Strategy Session
                    <ArrowRight className="h-5 w-5" />
                  </span>
                )}
              </Button>

              <p className="text-slate-500 text-xs text-center">
                No spam. No obligation. Just a conversation about how we can help.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Consultation;
